/**
 * Границы бизнес-суток — ЕДИНСТВЕННОЕ место работы с московским временем (§6.1).
 *
 * Живёт в @cc/shared, а не в @cc/domain/pure, потому что то же правило нужно
 * браузеру: apps/web зависит только от @cc/shared. `@cc/domain/pure/business-day.ts`
 * переэкспортирует эти функции — файл из §3 остаётся на месте, реализация одна.
 *
 * Вторая реализация здесь уже была и расходилась с доменной по двум пунктам:
 * смещение МСК было зашито константой +3 (ломается на первой же зоне с переходом
 * на летнее время), а `businessDayBounds` возвращала {from,to} вместо {fromUtc,toUtc},
 * то есть один и тот же вызов давал разные объекты в зависимости от места импорта.
 *
 * Сервер и Postgres живут в UTC. Если считать «сегодня» по времени сервера,
 * работа с 00:00 до 03:00 МСК уедет во вчерашний день: у ночной смены пропадёт
 * половина статистики, а «прозвонено» станет больше, чем «выдано».
 *
 * При неожиданном вводе — throw, никаких `?? 1` и `?? today`: молчаливый фоллбэк
 * даст диспетчеру чужой график, а отчёту — чужие сутки, и никто этого не заметит.
 *
 * Чистые функции: ни IO, ни Date.now() — момент времени всегда приходит аргументом.
 */
export const MOSCOW_TZ = 'Europe/Moscow';
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const ISO_WEEKDAY_BY_NAME = {
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
    Sun: 7,
};
function assertValidDate(at) {
    if (!(at instanceof Date) || Number.isNaN(at.getTime())) {
        throw new Error('businessDay: передан не Date или Invalid Date');
    }
}
/**
 * Насколько локальное время зоны опережает UTC в данный момент, в миллисекундах.
 * Считается через Intl, а не константой +3: константа сломается на первой же
 * зоне с переходом на летнее время, если проект переедет из МСК.
 */
function tzOffsetMs(at, tz) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).formatToParts(at);
    const get = (type) => {
        const part = parts.find((p) => p.type === type);
        if (!part)
            throw new Error(`businessDay: Intl не отдал часть ${type} для зоны ${tz}`);
        const n = Number(part.value);
        if (Number.isNaN(n))
            throw new Error(`businessDay: часть ${type} не число: ${part.value}`);
        return n;
    };
    const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
    return asUtc - Math.floor(at.getTime() / 1000) * 1000;
}
/** Бизнес-сутки момента времени в виде 'YYYY-MM-DD'. Ключ дневных витрин. */
export function businessDay(at, tz = MOSCOW_TZ) {
    assertValidDate(at);
    // en-CA даёт ровно YYYY-MM-DD без ручной склейки
    const formatted = new Intl.DateTimeFormat('en-CA', {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(at);
    if (!DATE_RE.test(formatted)) {
        throw new Error(`businessDay: неожиданный формат даты '${formatted}' для зоны ${tz}`);
    }
    return formatted;
}
/**
 * UTC-границы бизнес-суток: [fromUtc, toUtc). Для 2026-08-25 в МСК это
 * 2026-08-24T21:00:00Z … 2026-08-25T21:00:00Z.
 *
 * Верхняя граница считается как полночь СЛЕДУЮЩЕГО дня, а не «+24 часа»:
 * при переходе на летнее время сутки бывают короче и длиннее.
 */
export function businessDayBounds(day, tz = MOSCOW_TZ) {
    const fromUtc = localMidnightUtc(day, tz);
    const toUtc = localMidnightUtc(nextDay(day), tz);
    return { fromUtc, toUtc };
}
/** День недели в ISO: пн=1 … вс=7. Смещений нет: те же числа в БД и в графике. */
export function isoWeekday(at, tz = MOSCOW_TZ) {
    assertValidDate(at);
    const short = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short' }).format(at);
    const weekday = ISO_WEEKDAY_BY_NAME[short];
    if (weekday === undefined) {
        throw new Error(`isoWeekday: Intl вернул неизвестный день недели '${short}' для зоны ${tz}`);
    }
    return weekday;
}
/** Локальное время 'HH:MM:SS' в зоне — для сравнения с графиком смены. */
export function localTimeOfDay(at, tz = MOSCOW_TZ) {
    assertValidDate(at);
    return new Intl.DateTimeFormat('en-GB', {
        timeZone: tz,
        hourCycle: 'h23',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).format(at);
}
/** Момент времени по локальной дате и времени зоны. Нужен clampToWorkWindow. */
export function zonedTimeToUtc(day, timeOfDay, tz = MOSCOW_TZ) {
    const { h, m, s } = parseTimeOfDay(timeOfDay);
    const midnight = localMidnightUtc(day, tz);
    const naive = midnight.getTime() + (h * 3600 + m * 60 + s) * 1000;
    // повторная поправка: смещение зоны в полдень и в 23:00 может отличаться при DST
    const corrected = naive - (tzOffsetMs(new Date(naive), tz) - tzOffsetMs(midnight, tz));
    return new Date(corrected);
}
/** 'HH:MM' или 'HH:MM:SS' → компоненты. Формат кривой — throw, а не молчаливый ноль. */
export function parseTimeOfDay(value) {
    const match = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value);
    if (!match)
        throw new Error(`ожидалось время HH:MM или HH:MM:SS, получено '${value}'`);
    const h = Number(match[1]);
    const m = Number(match[2]);
    const s = match[3] === undefined ? 0 : Number(match[3]);
    if (h > 23 || m > 59 || s > 59)
        throw new Error(`время вне диапазона: '${value}'`);
    return { h, m, s };
}
/** Полночь локальной даты, выраженная в UTC. */
function localMidnightUtc(day, tz) {
    const { y, mo, d } = parseDay(day);
    const naive = Date.UTC(y, mo - 1, d, 0, 0, 0, 0);
    // две итерации: первая даёт приближение, вторая чинит случай перехода зоны
    let utcMs = naive - tzOffsetMs(new Date(naive), tz);
    utcMs = naive - tzOffsetMs(new Date(utcMs), tz);
    return new Date(utcMs);
}
function parseDay(day) {
    if (!DATE_RE.test(day))
        throw new Error(`ожидалась дата YYYY-MM-DD, получено '${day}'`);
    const y = Number(day.slice(0, 4));
    const mo = Number(day.slice(5, 7));
    const d = Number(day.slice(8, 10));
    if (mo < 1 || mo > 12 || d < 1 || d > 31)
        throw new Error(`дата вне диапазона: '${day}'`);
    return { y, mo, d };
}
function nextDay(day) {
    const { y, mo, d } = parseDay(day);
    const next = new Date(Date.UTC(y, mo - 1, d + 1));
    return next.toISOString().slice(0, 10);
}
//# sourceMappingURL=time.js.map